import { useEffect, useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Risk } from '@/components/views/Resolution886/enums';
import { useActionPlan } from '../../../components/ActionPlan/_hooks';
import { useResolution886Report } from '@/context/Resolution886Report';
import { ActionPlanResponse } from '@/context/Resolution886Report/types';
import { ErgonomicToolNames, EvaluationCategoryKeys } from '../../../enums';
import { useGetResolution886ReviewResults } from '@/hooks/v2/resolution-886/useGetResolution886ReviewResults';
import { useUpdateResolution886CategoryResult } from '@/hooks/v2/resolution-886/useUpdateResolution886CategoryResult';

interface EvaluationRiskParams {
	goToSubStep: (step: number) => void;
}

const categories = [
	{
		key: EvaluationCategoryKeys.LIFT_AND_LOWER,
		name: I18n.get('Lifting and lowering')
	},
	{
		key: EvaluationCategoryKeys.THERMAL_COMFORT,
		name: I18n.get('Thermal comfort')
	},
	{
		key: EvaluationCategoryKeys.REPETITIVE_MOVEMENTS,
		name: I18n.get('Repetitive movements')
	},
	{
		key: EvaluationCategoryKeys.HAND_ARM_VIBRATION,
		name: I18n.get('Hand-arm vibration')
	},
	{
		key: EvaluationCategoryKeys.PUSH_PULL,
		name: I18n.get('Push / Pull')
	},
	{
		key: EvaluationCategoryKeys.TRANSPORT,
		name: I18n.get('Manual load transport')
	},
	{
		key: EvaluationCategoryKeys.STANDING,
		name: I18n.get('Standing position')
	},
	{
		key: EvaluationCategoryKeys.FORCED_POSTURE,
		name: I18n.get('Forced posture')
	},
	{
		key: EvaluationCategoryKeys.WHOLE_BODY_VIBRATION,
		name: I18n.get('Whole body vibration')
	},
	{
		key: EvaluationCategoryKeys.CONTACT_STRESS,
		name: I18n.get('Contact stress')
	}
];

const ergonomicKeyNames = {
	[EvaluationCategoryKeys.LIFT_AND_LOWER]: ErgonomicToolNames.NIOSH,
	[EvaluationCategoryKeys.PUSH_PULL]: ErgonomicToolNames.KIM_PP,
	[EvaluationCategoryKeys.TRANSPORT]: ErgonomicToolNames.LIBERTY_MUTUAL,
	[EvaluationCategoryKeys.REPETITIVE_MOVEMENTS]: ErgonomicToolNames.STRAIN_INDEX,
	[EvaluationCategoryKeys.FORCED_POSTURE]: ErgonomicToolNames.REBA,
	[EvaluationCategoryKeys.WHOLE_BODY_VIBRATION]: ErgonomicToolNames.JDS_D86,
	[EvaluationCategoryKeys.HAND_ARM_VIBRATION]: ErgonomicToolNames.JDS_D86
};

export function useEvaluationRisk({ goToSubStep }: Readonly<EvaluationRiskParams>) {
	const [activeKey, setActiveKey] = useState<string | string[]>([]);
	const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
	const [openVideoDrawer, setOpenVideoDrawer] = useState<boolean>(false);
	const [categoryPrevious, setCategoryPrevious] = useState<any | null>(null);
	const [assessmentPrevious, setAssessmentPrevious] = useState<any | null>(null);

	const {
		form,
		report,
		assessment,
		categoryResultId,
		categoryId,
		actionPlan,
		openActionPlan,
		titleActionPlan,
		newReview,
		previousReview,
		relatedReport,
		handleActionPlan,
		handleSetActionPlan
	} = useResolution886Report();

	const { isLoading: isLoadingUpdateCategoryResult, mutate: updateCategoryResult } =
		useUpdateResolution886CategoryResult();

	const organization_id = form.getFieldValue(['general_information', 'organization_id']);
	const company_id = form.getFieldValue(['general_information', 'company_id']);
	const review_id = form.getFieldValue(['general_information', 'review_id']);

	const { data: previousReviewResult } = useGetResolution886ReviewResults({
		organization_id,
		company_id,
		review_id: (previousReview && previousReview?.id) || '',
		report_id: report!.id
	});

	const { handleOnDelete } = useActionPlan({
		organization_id,
		company_id,
		review_id,
		report_id: report!.id,
		activity_id: assessment!.activity!.id
	});

	const category = categories.find((category) => category.key === categoryId);

	function handleOpenModalDelete(): void {
		setOpenModalDelete(true);
	}

	function handleOpenVideoDrawer(): void {
		setOpenVideoDrawer(true);
	}

	async function handleUpdateCategoryResult() {
		const observations = form.getFieldValue(['evaluation_risk', 'observations']);
		const risk_level = form.getFieldValue(['evaluation_risk', 'risk_level']);

		const observations_trimmed = observations ? String(observations).trim() : null;

		const payload = {
			assessment_id: assessment?.id,
			organization_id,
			company_id,
			category_key: categoryId,
			category_result_id: categoryResultId as string,
			category_result_risk: risk_level as Risk,
			category_result_observation: observations_trimmed !== '' ? observations_trimmed : null
		};

		updateCategoryResult(payload, {
			onSuccess: () => {
				goToSubStep(2);
			}
		});
	}

	function handleAddActionPlan(): void {
		const title = assessment.activity?.name + ' - ' + I18n.get(category?.name);
		handleActionPlan(title);
	}

	function handleOnDeleteActionPlan(): void {
		handleOnDelete(actionPlan!.id);
		setOpenModalDelete(false);
		handleSetActionPlan({} as ActionPlanResponse);
	}

	function handleOnToggleCollapse(): void {
		setActiveKey(activeKey.length ? [] : ['1']);
	}

	useEffect(() => {
		if (previousReviewResult) {
			setAssessmentPrevious(
				previousReviewResult?.assessments?.find((ass) => ass?.activity!.id === assessment!.activity!.id) || null
			);
		}
	}, [previousReviewResult, assessment]);

	useEffect(() => {
		if (assessmentPrevious) {
			setCategoryPrevious(
				assessmentPrevious?.category_results?.find((item: any) => item.category.key === categoryId) || null
			);
		}
	}, [assessmentPrevious]);

	const risk = form.getFieldValue(['evaluation_risk', 'risk_level']);

	const isLoading = isLoadingUpdateCategoryResult;

	return {
		risk,
		activeKey,
		newReview,
		previousReview,
		assessment,
		category,
		actionPlan,
		openActionPlan,
		titleActionPlan,
		isLoading,
		openModalDelete,
		openVideoDrawer,
		categoryPrevious,
		relatedReport,
		ergonomicKeyNames,
		handleOpenModalDelete,
		handleOpenVideoDrawer,
		handleActionPlan,
		setOpenModalDelete,
		setOpenVideoDrawer,
		handleUpdateCategoryResult,
		handleAddActionPlan,
		handleOnDeleteActionPlan,
		handleOnToggleCollapse
	};
}
